import {
  Box,
  CircularProgress,
  IconButton,
  Typography
} from '@material-ui/core'
import { Send } from '@material-ui/icons'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import MicRecorder from 'mic-recorder-to-mp3'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import RecordingTimer from '../../components/MessageInputCustom/RecordingTimer'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import { useDate } from '../../hooks/useDate'
import api from '../../services/api'
import { useStyles } from './style'

import ChatDrawerHeader from '../ChatDrawerHeader'
import ChatMessage from '../ChatMessage'
import ChatMessageActionsMenu from '../ChatMessageActionsMenu'
import ChatMessageReactionsMenu from '../ChatMessageReactionsMenu'
import FloatingActionMenu from '../FloatingActionMenu'
import { InputField } from '../InputField'
import { UploadChatDropZone } from '../UploadChatDropZone'
import { SendHorizontal } from 'lucide-react'

const Mp3Recorder = new MicRecorder({ bitRate: 128 })

const ChatDrawerMessages = ({ user, chat, onClose }) => {
  const classes = useStyles()

  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [recording, setRecording] = useState(false)
  const [showEmoji, setShowEmoji] = useState(false)
  const [fabMenuOpen, setFabMenuOpen] = useState(false)
  const [currentChatId, setCurrentChatId] = useState(null)
  const [quotedMessage, setQuotedMessage] = useState(null)

  const textFieldRef = useRef(null)

  const messagesEndRef = useRef(null)
  const messagesListRef = useRef(null)

  const socketManager = useContext(SocketContext)
  const { user: currentUser } = useContext(AuthContext)
  const { datetimeToClient } = useDate()

  const isGroupChat = !!chat
  const chatTitle = isGroupChat ? chat.title : user?.name || 'Chat'
  const chatId = isGroupChat ? chat.id : currentChatId

  const [participantsText, setParticipantsText] = useState('')

  const [actionsMenuOpen, setActionsMenuOpen] = useState(false)
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null)
  const [selectedMessageForActions, setSelectedMessageForActions] =
    useState(null)

  const [reactionsMenuOpen, setReactionsMenuOpen] = useState(false)
  const [reactionsAnchorEl, setReactionsAnchorEl] = useState(null)
  const [selectedMessageForReactions, setSelectedMessageForReactions] =
    useState(null)

  const [hoveredMessageId, setHoveredMessageId] = useState(null)

  const fabMenuRef = useRef(null)
  const fileInputRef = useRef(null)
  const hoverTimeoutRef = useRef(null)

  const handleMouseEnter = useCallback(messageId => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
    }
    setHoveredMessageId(messageId)
  }, [])

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredMessageId(null)
    }, 100) // Delay de 100ms
  }, [])

  const handleOpenActionsMenu = (event, message) => {
    event.preventDefault()
    event.stopPropagation()

    const menuPosition = {
      mouseX: event.clientX - 8,
      mouseY: event.clientY - 4
    }
    setActionsAnchorEl(menuPosition)
    setSelectedMessageForActions(message)
    setActionsMenuOpen(true)
  }

  const handleCloseActionsMenu = () => {
    setActionsMenuOpen(false)
    setActionsAnchorEl(null)
    setSelectedMessageForActions(null)
  }

  const handleOpenReactionsFromActions = (anchorPosition, message) => {
    setReactionsAnchorEl(anchorPosition)
    setSelectedMessageForReactions(message)
    setReactionsMenuOpen(true)
  }

  const handleReplyMessage = message => {
    setQuotedMessage(message)
    if (textFieldRef.current) {
      textFieldRef.current.focus()
    }
  }

  const processReactionsInMessages = messages => {
    const messagesWithReplies = messages.map(message => {
      if (message.mediaType === 'reactionMessage') {
        return message
      }

      const replies = messages.filter(
        msg =>
          msg.mediaType === 'reactionMessage' && msg.quotedId === message.id
      )

      return {
        ...message,
        replies: replies.length > 0 ? replies : message.replies || []
      }
    })
    const finalMessages = messagesWithReplies.filter(
      msg => msg.mediaType !== 'reactionMessage'
    )
    return finalMessages
  }

  useEffect(() => {
    const fetchMessages = async () => {
      if ((!user && !chat) || loading) {
        return
      }

      setLoading(true)
      try {
        let chatIdToUse = null
        let messagesData = []

        if (isGroupChat) {
          chatIdToUse = chat.id
          const { data } = await api.get(`/chats/${chatIdToUse}/messages`)
          messagesData = data.records || []
        } else {
          const { data: chatsData } = await api.get('/chats')

          if (chatsData && chatsData.records) {
            const userChat = chatsData.records.find(chat => {
              if (chat.users.length === 2) {
                return chat.users.some(u => u.userId === user.id)
              }
              return false
            })

            if (userChat) {
              chatIdToUse = userChat.id
              const { data } = await api.get(`/chats/${chatIdToUse}/messages`)
              messagesData = data.records || []
            }
          }
        }

        const processedMessages = processReactionsInMessages(messagesData)

        setCurrentChatId(chatIdToUse)
        setMessages(processedMessages)
        setLoading(false)
        scrollToBottom()
      } catch (err) {
        toastError(err)
        setLoading(false)
      }
    }

    fetchMessages()
  }, [user, chat, isGroupChat])

  useEffect(() => {
    if (!user && !chat) {
      return
    }

    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const updateMessagesWithNewData = incomingMessage => {
      setMessages(prev => {
        const newMessages = [...prev]

        if (
          incomingMessage.mediaType === 'reactionMessage' &&
          incomingMessage.quotedId
        ) {
          const messageIndex = newMessages.findIndex(
            msg => msg.id === incomingMessage.quotedId
          )

          if (messageIndex !== -1) {
            const messageToUpdate = { ...newMessages[messageIndex] }
            const existingReplies = messageToUpdate.replies || []
            const reactionAlreadyExists = existingReplies.some(
              reply => reply.id === incomingMessage.id
            )

            if (!reactionAlreadyExists) {
              messageToUpdate.replies = [
                ...existingReplies.filter(reply => !reply.temporary),
                {
                  id: incomingMessage.id,
                  message: incomingMessage.message,
                  sender: incomingMessage.sender || {
                    name:
                      incomingMessage.senderId === currentUser.id
                        ? currentUser.name
                        : 'Usuário'
                  },
                  mediaType: incomingMessage.mediaType,
                  quotedId: incomingMessage.quotedId,
                  senderId: incomingMessage.senderId
                }
              ]
              newMessages[messageIndex] = messageToUpdate
            }
          }
        } else {
          const messageIndex = newMessages.findIndex(
            msg => msg.id === incomingMessage.id
          )
          if (messageIndex === -1) {
            if (
              incomingMessage.mediaPath &&
              (!incomingMessage.message || incomingMessage.message === 'blob')
            ) {
              const fileName = incomingMessage.originalFileName || 'Arquivo'
              incomingMessage = { ...incomingMessage, message: fileName }
            }
            newMessages.push(incomingMessage)
          } else {
            //adicionado para quando a o retorno do mediaPath for undefined utiliza o estado anterior e adiciona o novo para não perder a propriedade do mediaPath, com isso a imagem não some ao reagir a uma imagem
            const existingMessage = newMessages[messageIndex]
            newMessages[messageIndex] = {
              ...existingMessage,
              ...incomingMessage
            }
          }
        }
        return newMessages
      })
      scrollToBottom()
    }

    const onNewMessage = data => {
      const isForThisChat = isGroupChat
        ? data.chatId === chat?.id
        : data.chatId === currentChatId ||
          (data.senderId === user?.id && data.recipientId === currentUser.id) ||
          (data.senderId === currentUser.id && data.recipientId === user?.id)

      if (isForThisChat) {
        updateMessagesWithNewData(data)
      }
    }

    socket.on('chat-message', onNewMessage)

    if (chatId) {
      socket.on(`company-${companyId}-chat-${chatId}`, data => {
        if (data.action === 'new-message') {
          updateMessagesWithNewData(data.newMessage)
        }
      })
    }

    return () => {
      socket.off('chat-message')
      if (chatId) {
        socket.off(`company-${companyId}-chat-${chatId}`)
      }
    }
  }, [
    socketManager,
    user,
    chat,
    currentUser,
    isGroupChat,
    chatId,
    currentChatId
  ])

  useEffect(() => {
    if (textFieldRef.current) {
      textFieldRef.current?.focus()
    }
  }, [])

  useEffect(() => {
    if (isGroupChat && chat && chat.users && chat.users.length > 0) {
      const participants = chat.users
        .map(u => u.user?.name || u.name)
        .filter(Boolean)

      if (participants.length > 0) {
        const maxNamesToShow = 3
        let displayText = ''

        if (participants.length <= maxNamesToShow) {
          displayText = participants.join(', ')
        } else {
          displayText = `${participants
            .slice(0, maxNamesToShow)
            .join(', ')} e mais ${participants.length - maxNamesToShow}`
        }

        setParticipantsText(displayText)
      } else {
        setParticipantsText('')
      }
    } else {
      setParticipantsText('')
    }
  }, [chat, isGroupChat])

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const findOrCreateChat = async () => {
    if (isGroupChat && chat && chat.id) return chat.id

    if (currentChatId) return currentChatId

    try {
      const { data: chatsData } = await api.get('/chats')

      if (!chatsData || !chatsData.records) {
        console.error('Erro: dados de chat inválidos recebidos da API')
        return null
      }

      const existingChat = chatsData.records.find(
        c =>
          c.users &&
          c.users.length === 2 &&
          c.users.some(u => u.userId === user.id)
      )

      if (existingChat) {
        setCurrentChatId(existingChat.id)
        return existingChat.id
      }

      if (!user || !user.id || !user.name) {
        console.error('Erro: dados de usuário inválidos para criar chat')
        return null
      }

      const { data: newChat } = await api.post('/chats', {
        users: [{ id: user.id, name: user.name }],
        title: `Chat com ${user.name}`
      })

      if (!newChat || !newChat.id) {
        console.error('Erro: falha ao criar novo chat')
        return null
      }

      setCurrentChatId(newChat.id)
      return newChat.id
    } catch (err) {
      console.error('Erro ao encontrar ou criar chat:', err)
      toastError(err)
      return null
    }
  }

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || (!user && !chat)) return

    const messageText = inputMessage.trim()
    const quotedId = quotedMessage ? quotedMessage.id : null

    setInputMessage('')
    setQuotedMessage(null)

    try {
      const chatIdToPost = await findOrCreateChat()
      if (!chatIdToPost) {
        console.error('Não foi possível encontrar ou criar um chat')
        return
      }

      await api.post(`/chats/${chatIdToPost}/messages`, {
        message: messageText,
        quotedId
      })
    } catch (err) {
      console.error('Erro ao enviar mensagem:', err)
      toastError(err)
    }
  }

  const handleStartRecording = async () => {
    setFabMenuOpen(false)
    setLoading(true)
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true })
      await Mp3Recorder.start()
      setRecording(true)
      setLoading(false)
    } catch (err) {
      toastError(err)
      setLoading(false)
    }
  }

  const handleUploadAudio = async () => {
    if (!user && !chat) return
    setLoading(true)

    try {
      const [, blob] = await Mp3Recorder.stop().getMp3()

      if (blob.size < 10000) {
        setLoading(false)
        setRecording(false)
        return
      }

      const chatIdToPost = await findOrCreateChat()
      if (!chatIdToPost) {
        setLoading(false)
        setRecording(false)
        return
      }

      const formData = new FormData()
      const filename = `audio-${new Date().getTime()}.mp3`

      formData.append('medias', blob, filename)
      formData.append('body', '')
      formData.append('fromMe', true)

      await api.post(`/chats/${chatIdToPost}/messages`, formData)

      scrollToBottom()
    } catch (err) {
      toastError(err)
    }

    setRecording(false)
    setLoading(false)
  }

  const handleCancelAudio = async () => {
    try {
      await Mp3Recorder.stop().getMp3()
      setRecording(false)
    } catch (err) {
      toastError(err)
    }
  }

  const handleAddEmoji = useCallback(
    e => {
      const emoji = e.native
      // No TextField com inputRef, o current JÁ É o elemento de texto
      const input = textFieldRef.current

      if (input) {
        // Pegamos as posições de seleção (funciona em input e textarea)
        const start = input.selectionStart
        const end = input.selectionEnd

        // Fatiamos o texto atual para inserir o emoji
        const textBefore = inputMessage.substring(0, start)
        const textAfter = inputMessage.substring(end)

        const newMessage = textBefore + emoji + textAfter

        setInputMessage(newMessage)

        // Aguardamos o ciclo de renderização para devolver o foco e mover o cursor
        setTimeout(() => {
          input.focus()
          // Posicionamos o cursor logo após o emoji inserido
          const newPos = start + emoji.length
          input.setSelectionRange(newPos, newPos)
        }, 0)
      } else {
        // Fallback caso a Ref ainda não tenha sido atribuída
        setInputMessage(prevState => prevState + emoji)
      }
    },
    [inputMessage]
  )

  const handleToggleFabMenu = () => {
    setFabMenuOpen(!fabMenuOpen)
  }

  const handleUpdateChat = async updatedChat => {
    try {
      await api.get(`/chats/${chat.id}`)
    } catch (err) {
      toastError(err)
    }
  }

  const handleAddReaction = async (emoji, message) => {
    try {
      const payload = {
        message: emoji,
        quotedId: message.id,
        mediaType: 'reactionMessage'
      }

      const chatIdToPost = chatId || (await findOrCreateChat())
      if (!chatIdToPost) {
        console.error('Não foi possível obter chatId para adicionar reação.')
        return
      }

      await api.post(`/chats/${chatIdToPost}/messages`, payload)
    } catch (err) {
      console.error('Erro ao adicionar reação:', err)
      toastError(err)

      setMessages(prev => {
        const newMessages = [...prev]
        const messageIndex = newMessages.findIndex(msg => msg.id === message.id)

        if (messageIndex !== -1 && newMessages[messageIndex].replies) {
          const filteredReplies = newMessages[messageIndex].replies.filter(
            reply => !reply.temporary
          )
          if (
            filteredReplies.length !== newMessages[messageIndex].replies.length
          ) {
            newMessages[messageIndex].replies = filteredReplies
          }
        }
        return newMessages
      })
    }
  }

  const handleMessageDeleted = useCallback(deletedMessageId => {
    setMessages(prevMessages => {
      const updatedMessages = prevMessages.filter(
        msg => msg.id !== deletedMessageId
      )
      return updatedMessages
    })
  }, [])

  const handleFileUploadComplete = useCallback(() => {
    scrollToBottom()
  }, [scrollToBottom])

  return (
    <UploadChatDropZone
      chatId={chatId}
      chatStatus={chat?.status || 'open'}
      isGroupChat={isGroupChat}
      fileInputRef={fileInputRef}
      onFileUploadComplete={handleFileUploadComplete}
    >
      <Box className={classes.messagesContainer}>
        <ChatDrawerHeader
          user={user}
          chat={chat}
          onClose={onClose}
          isGroupChat={isGroupChat}
          chatTitle={chatTitle}
          participantsText={participantsText}
          handleUpdateChat={handleUpdateChat}
        />
        <div ref={messagesListRef} className={classes.messagesList}>
          {loading && messages.length === 0 ? (
            <Box display="flex" justifyContent="center" m={2}>
              <CircularProgress size={24} />
            </Box>
          ) : messages.length === 0 ? (
            <Box className={classes.noMessages}>
              <Typography variant="body2">
                Nenhuma mensagem ainda. Comece a conversar!
              </Typography>
            </Box>
          ) : (
            messages.map(message => (
              <ChatMessage
                key={`message-${message.id}`}
                message={message}
                currentUser={currentUser}
                isGroupChat={isGroupChat}
                chatTitle={chatTitle}
                hoveredMessageId={hoveredMessageId}
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                handleOpenActionsMenu={handleOpenActionsMenu}
                handleOpenReactionsFromActions={handleOpenReactionsFromActions}
                datetimeToClient={datetimeToClient}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {selectedMessageForReactions && (
          <ChatMessageReactionsMenu
            message={selectedMessageForReactions}
            menuOpen={reactionsMenuOpen}
            handleClose={() => {
              setReactionsMenuOpen(false)
              setReactionsAnchorEl(null)
              setSelectedMessageForReactions(null)
            }}
            anchorEl={reactionsAnchorEl}
            onAddReaction={handleAddReaction}
          />
        )}

        {selectedMessageForActions && (
          <ChatMessageActionsMenu
            message={selectedMessageForActions}
            menuOpen={actionsMenuOpen}
            handleClose={handleCloseActionsMenu}
            anchorEl={actionsAnchorEl}
            onReplyMessage={handleReplyMessage}
            onOpenReactionsMenu={handleOpenReactionsFromActions}
            onMessageDeleted={handleMessageDeleted}
          />
        )}

        {recording ? (
          <div className={classes.recorderWrapper}>
            <IconButton
              aria-label="cancelRecording"
              component="span"
              disabled={loading}
              onClick={handleCancelAudio}
            >
              <HighlightOffIcon className={classes.cancelAudioIcon} />
            </IconButton>
            {loading ? (
              <div>
                <CircularProgress className={classes.audioLoading} size={24} />
              </div>
            ) : (
              <RecordingTimer />
            )}

            <IconButton
              aria-label="sendRecordedAudio"
              component="span"
              onClick={handleUploadAudio}
              disabled={loading}
            >
              <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
            </IconButton>
          </div>
        ) : (
          <>
            {quotedMessage && (
              <Box className={classes.quotedMessageContainer}>
                <div className={classes.quotedMessage}>
                  {quotedMessage.message}
                </div>
                <IconButton size="small" onClick={() => setQuotedMessage(null)}>
                  <HighlightOffIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box className={classes.inputContainer}>
              <FloatingActionMenu
                loading={loading}
                recording={recording}
                showEmoji={showEmoji}
                setShowEmoji={setShowEmoji}
                handleAddEmoji={handleAddEmoji}
                handleStartRecording={handleStartRecording}
                handleToggleFabMenu={handleToggleFabMenu}
                fabMenuOpen={fabMenuOpen}
                fabMenuRef={fabMenuRef}
              />

              <InputField
                className={classes.messageInput}
                variant="outlined"
                size="small"
                emoji={false}
                placeholder="Digite sua mensagem..."
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyPress={e => {
                  if (e.shiftKey) return

                  if (e.key === 'Enter') {
                    handleSendMessage()
                  }
                }}
                inputRef={textFieldRef}
              />

              <IconButton
                className={classes.sendButton}
                onClick={handleSendMessage}
                disabled={loading}
              >
                <SendHorizontal size={18}/>
              </IconButton>
            </Box>
          </>
        )}
      </Box>
    </UploadChatDropZone>
  )
}

export default ChatDrawerMessages
